import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ArrowLeftRight, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader, StockBadge, Pager, TH } from "@/components/common";
import { ArticleDialog } from "@/components/ArticleDialog";
import { ScanHub } from "@/components/ScanHub";
import { api, apiError, fmtQty, download } from "@/lib/api";

const ALL = "__all__";

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-12 w-full sm:w-48" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Articles() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debQ, setDebQ] = useState("");
  const [filters, setFilters] = useState({ categorie: "", emplacement: "", sous_seuil: false });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });
  const [facets, setFacets] = useState({ categories: [], emplacements: [], unites: [] });
  const [dialog, setDialog] = useState({ open: false, article: null });
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setDebQ(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (params.get("new")) { setDialog({ open: true, article: null }); setParams({}, { replace: true }); } }, [params, setParams]);

  const load = useCallback(() => {
    api.get("/articles", { params: { q: debQ, ...filters, page, page_size: 25 } }).then((r) => setData(r.data));
  }, [debQ, filters, page]);
  const loadFacets = () => api.get("/articles/facets").then((r) => setFacets(r.data));
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("sk-stock-changed", load);
    return () => window.removeEventListener("sk-stock-changed", load);
  }, [load]);
  useEffect(() => { loadFacets(); }, []);

  const setFilter = (k, v) => { setFilters({ ...filters, [k]: v }); setPage(1); };

  const confirmDelete = async () => {
    try {
      await api.delete(`/articles/${encodeURIComponent(toDelete.code)}`);
      toast.success(`Article ${toDelete.code} supprimé`);
      load();
    } catch (e) {
      toast.error(apiError(e), { duration: 7000 });
    }
    setToDelete(null);
  };

  return (
    <div data-testid="articles-page">
      <PageHeader title="Articles" subtitle="Catalogue des articles simples. Le stock évolue uniquement par mouvements." testid="articles-title">
        <Button variant="outline" className="h-12 gap-2" onClick={() => download("/export/articles", "articles_stockkit.csv")} data-testid="articles-export-button"><Download className="h-4 w-4" /> Exporter CSV</Button>
        <Button className="h-12 gap-2" onClick={() => setDialog({ open: true, article: null })} data-testid="articles-new-button"><Plus className="h-4 w-4" /> Nouvel article</Button>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 rounded-md border bg-white p-4 shadow-sm lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par code, désignation ou code-barres…" className="h-12 pl-10" data-testid="articles-search-input" />
        </div>
        <FilterSelect value={filters.categorie} onChange={(v) => setFilter("categorie", v)} options={facets.categories} placeholder="Toutes catégories" testid="articles-filter-categorie" />
        <FilterSelect value={filters.emplacement} onChange={(v) => setFilter("emplacement", v)} options={facets.emplacements} placeholder="Tous emplacements" testid="articles-filter-emplacement" />
        <label className="flex h-12 cursor-pointer items-center gap-3 rounded-md border px-4 text-sm font-medium">
          <Switch checked={filters.sous_seuil} onCheckedChange={(v) => setFilter("sous_seuil", v)} data-testid="articles-filter-sous-seuil" /> Sous seuil
        </label>
      </div>

      <div className="rounded-md border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table data-testid="articles-table">
            <TableHeader>
              <TableRow>
                <TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead><TableHead className={TH}>Code-barres</TableHead>
                <TableHead className={TH}>Catégorie</TableHead><TableHead className={TH}>Emplacement</TableHead>
                <TableHead className={`${TH} text-right`}>Stock</TableHead><TableHead className={`${TH} text-right`}>Seuil</TableHead><TableHead className={TH}>Statut</TableHead><TableHead className={`${TH} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-slate-500" data-testid="articles-empty">Aucun article trouvé.</TableCell></TableRow>}
              {data.items.map((a) => (
                <TableRow key={a.code} data-testid={`article-row-${a.code}`}>
                  <TableCell className="font-mono font-semibold">{a.code}</TableCell>
                  <TableCell className="min-w-[200px]">{a.designation}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{a.barcode || "—"}</TableCell>
                  <TableCell className="text-sm">{a.categorie || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{a.emplacement || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right font-mono font-semibold" data-testid={`article-stock-${a.code}`}>{fmtQty(a.stock)} <span className="text-xs font-normal text-slate-400">{a.unite}</span></TableCell>
                  <TableCell className="text-right font-mono text-slate-500">{fmtQty(a.seuil_min)}</TableCell>
                  <TableCell><StockBadge stock={a.stock} seuil={a.seuil_min} testid={`article-status-${a.code}`} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" className="h-11 w-11 p-0" title="Mouvement" onClick={() => navigate(`/mouvements?code=${encodeURIComponent(a.code)}`)} data-testid={`article-move-${a.code}`}><ArrowLeftRight className="h-4 w-4" /></Button>
                      <Button variant="ghost" className="h-11 w-11 p-0" title="Modifier" onClick={() => setDialog({ open: true, article: a })} data-testid={`article-edit-${a.code}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" className="h-11 w-11 p-0 text-rose-600 hover:text-rose-700" title="Supprimer" onClick={() => setToDelete(a)} data-testid={`article-delete-${a.code}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} testid="articles-pager" />
      </div>

      <ScanHub enabled={!dialog.open && !toDelete} onChanged={load} />
      <ArticleDialog open={dialog.open} article={dialog.article} facets={facets} onOpenChange={(o) => setDialog({ ...dialog, open: o })} onSaved={() => { load(); loadFacets(); }} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="article-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'article {toDelete?.code} ?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.designation} — l'historique des mouvements est conservé. Un article utilisé dans un kit ne peut pas être supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11" data-testid="article-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="h-11 bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="article-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
